import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// Color design tokens
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        warnings: {
          "blue": "#56B8D0",
          "red": "#EC7A99",
          "green": "#4EBF85",
          "orange": "#FA9138"
        },

        palette: {
          0: "#324A51",
          1: "#56B8D0",
          2: "#9C6D4B",
          3: "#D4A17C",
          4: "#96B0B7",
          5: "#BFA0D3",
          6: "#896C9C"
        },

        chartColors: [
          "#37ABC8",  // Cyan - used for Duration
          "#B690C6",  // Light purple - used for Altitude/Elevation
          "#D4A574",  // Wheat/gold - used for Speed
          "#E67E99",  // Pink/coral - used for Gradient
          "#89F9BA",  // Mint green - used for Power
          "#7E2D00",  // Dark brown - used for Assumed CdA
          "#907159",  // Light brown - used for Calculated CdA
          "#334A52",  // Dark teal - used for Position
          "#00664D",  // Forest green - used for Twistiness/Curvature
          "#815D90",  // Purple
          "#142A25",  // Dark green
          "#F4A261",  // Warm orange
          "#6A8EAE",  // Soft blue
          "#C96480",  // Rose pink
          "#7FB069",  // Sage green
          "#A67C52",  // Tan/camel
          "#5D8AA8",  // Air force blue
          "#EEE8A9",  // Pale yellow
          "#9B6B9E",  // Mauve
          "#4A8895"   // Teal blue
        ],

        grey: {
          100: "#cfdcdf",
          200: "#bccdd2",
          300: "#a9bfc5",
          400: "#7f9399",
          500: "#6f8186",
          600: "#505c60",
          700: "#303739",
          800: "#202526",
          900: "#101213",
        },

        primary: {
          100: "#cfdcdf",
          200: "#bccdd2",
          300: "#a9bfc5",
          400: "#7f9399",
          500: "#6f8186",
          600: "#505c60",
          700: "#303739",
          800: "#202526",
          900: "#101213",
        },

        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },

        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },

        blueAccent: {
          100: "#a8daed",
          200: "#86bdd4",
          300: "#60a0ba",
          400: "#428ba6",
          500: "#187693",
          600: "#006782",
          700: "#00546c",
          800: "#004157",
          900: "#002d40",
        },
      }
    : {
        warnings: {
          "blue": "#56B8D0",
          "red": "#EC7A99",
          "green": "#4EBF85",
          "orange": "#FA9138"
        },

        palette: {
          0: "#324A51",
          1: "#56B8D0",
          2: "#9C6D4B",
          3: "#D4A17C",
          4: "#96B0B7",
          5: "#BFA0D3",
          6: "#896C9C"
        },

        chartColors: [
          "#37ABC8",  // Cyan - used for Duration
          "#B690C6",  // Light purple - used for Altitude/Elevation
          "#D4A574",  // Wheat/gold - used for Speed
          "#E67E99",  // Pink/coral - used for Gradient
          "#89F9BA",  // Mint green - used for Power
          "#7E2D00",  // Dark brown - used for Assumed CdA
          "#907159",  // Light brown - used for Calculated CdA
          "#334A52",  // Dark teal - used for Position
          "#00664D",  // Forest green - used for Twistiness/Curvature
          "#815D90",  // Purple
          "#142A25",  // Dark green
          "#F4A261",  // Warm orange
          "#6A8EAE",  // Soft blue
          "#C96480",  // Rose pink
          "#7FB069",  // Sage green
          "#A67C52",  // Tan/camel
          "#5D8AA8",  // Air force blue
          "#EEE8A9",  // Pale yellow
          "#9B6B9E",  // Mauve
          "#4A8895"   // Teal blue
        ],

        grey: {
          100: "#404a4c",
          200: "#5f6e73",
          300: "#7f9399",
          400: "#a9bfc5",
          500: "#bccdd2",
          600: "#cfdcdf",
          700: "#e2eaec",
          800: "#ecf1f2",
          900: "#f5f8f9",
        },

        primary: {
          100: "#404a4c",
          200: "#5f6e73",
          300: "#7f9399",
          400: "#a9bfc5",
          500: "#bccdd2",
          600: "#cfdcdf",
          700: "#e2eaec",
          800: "#ecf1f2",
          900: "#f5f8f9",
        },

        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },

        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },

        blueAccent: {
          100: "#002d40",
          200: "#004157",
          300: "#00546c",
          400: "#006782",
          500: "#187693",
          600: "#428ba6",
          700: "#60a0ba",
          800: "#86bdd4",
          900: "#a8daed",
        },
      }),
});

// MUI theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.grey[900],
            },
            DataGrid: {
              headerBg: colors.grey[700],
            },
          }
        : {
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.grey[900],
            },
            DataGrid: {
              headerBg: colors.grey[700],
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 14,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: "2rem", // 32px on mobile
        "@media (min-width:600px)": {
          fontSize: "2.5rem", // 40px on desktop
        },
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: "1.75rem", // 28px on mobile
        "@media (min-width:600px)": {
          fontSize: "2rem", // 32px on desktop
        },
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: "1.5rem", // 24px (same, reasonable for mobile)
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: "1.125rem", // 18px on mobile
        "@media (min-width:600px)": {
          fontSize: "1.25rem", // 20px on desktop
        },
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: "1rem", // 16px (same, good for mobile)
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: "0.875rem", // 14px (same, good for mobile)
      },
    },
    
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: colors.grey[800],
          }
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            background: colors.grey[800],
          }
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            background: colors.grey[800],
          }
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            background: colors.grey[800],
          }
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: "0",
            color: `${colors.grey[200]} !important`,
            '--DataGrid-containerBackground': colors.grey[700],
          },
          cell: {
            borderBottom: "none",
          },
          columnHeaders: {
            backgroundColor: colors.grey[800],
            borderBottom: "2",
          },
          columnHeaderTitle: {
            fontWeight: "bold !important",
          },
          virtualScroller: {
            backgroundColor: colors.grey[800],
          },
          footerContainer: {
            borderTop: "2",
            backgroundColor: colors.grey[700],
          },
          toolbarContainer: {
            '& .MuiButton-text': {
              color:`${colors.grey[100]} !important`,
            },
          },
        },
      },
    },
  };
};

// Context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  setMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode ? savedMode : "dark";
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => {
          const newMode = prev === "light" ? "dark" : "light";
          localStorage.setItem("themeMode", newMode);
          return newMode;
        });
      },
      setMode: (newMode) => {
        setMode(newMode);
        localStorage.setItem("themeMode", newMode);
      },
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
